import { Box } from '@mui/material'
import {
  MRT_ColumnDef as MRTColumnDef,
  MRT_RowData as MRTRowData,
  MaterialReactTable,
  MaterialReactTableProps,
  useMaterialReactTable,
} from 'material-react-table'

type IDataGridProps<TData extends MRTRowData> = MaterialReactTableProps<TData> & {
  contianerStyles?: React.CSSProperties
  leftHeadingComponent?: React.ReactElement
  rightHeadingComponent?: React.ReactElement
  componentPadding?: string
  componentMargin?: string | number
  columns: MRTColumnDef<TData, any>[] // Make columns explicitly required
  withChild?: boolean
  // Generic toolbar content
  toolbarContent?: React.ReactNode
  // Position for the toolbar content - allows flexibility in placement
  toolbarContentPosition?: 'left' | 'center' | 'right' | 'full-width'
}

// eslint-disable-next-line
export const MRTDataGridV2 = <TData extends MRTRowData = {}>(props: IDataGridProps<TData>) => {
  const styles = {
    boxStyles: {
      borderRadius: '3px',
    },
  }

  const {
    contianerStyles,
    leftHeadingComponent,
    rightHeadingComponent,
    enableRowSelection,
    componentPadding,
    componentMargin,
    enableDensityToggle = true,
    state,
    withChild = false,
    toolbarContent,
    toolbarContentPosition = 'full-width',
    ...rest
  } = props

  const table = useMaterialReactTable({
    // Zebra striping applied at tbody level so a single CSS class targets cells directly,
    // bypassing MRT v2's per-cell background that would otherwise cover row-level colours.
    muiTableBodyProps: {
      sx: withChild ? {} : {
        '& tr:nth-of-type(odd) td': { backgroundColor: '#E7E7E7 !important' },
        '& tr:nth-of-type(even) td': { backgroundColor: '#FFFFFF !important' },
        '& tr:hover td': { backgroundColor: '#E0E0E0 !important' },
      },
    },
    muiTablePaperProps: ({ table }) => ({
      style: {
        zIndex: table.getState().isFullScreen ? 99999 : undefined,
      },
    }),
    muiTableHeadCellProps: {
      sx: {
        color: '#636363',
        fontSize: '14px',
      },
    },
    muiTopToolbarProps: {
      sx: {
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        flexWrap: 'wrap',
        paddingLeft: '8px',
        paddingRight: '8px',
      },
    },
    renderTopToolbarCustomActions: toolbarContent
      ? () => {
        return (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexGrow: toolbarContentPosition === 'full-width' ? 1 : 0,
              marginLeft: toolbarContentPosition === 'center' ? 'auto' : 0,
              marginRight:
                toolbarContentPosition === 'center' || toolbarContentPosition === 'left'
                  ? 'auto'
                  : 0,
            }}
          >
            {toolbarContent}
          </Box>
        )
      }
      : undefined,
    globalFilterFn: 'contains',
    ...{
      ...rest,
      enableRowSelection,
      enableDensityToggle,
      state: { ...state },
      initialState: {
        ...(rest.initialState || {}),
        density: rest.initialState?.density || 'compact',
      },
    },
  })

  return (
    <Box sx={styles.boxStyles}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: componentPadding || '0 0 18px 0',
          margin: componentMargin || 0
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>{leftHeadingComponent}</Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>{rightHeadingComponent}</Box>
      </Box>
      <Box style={contianerStyles} sx={{ flexGrow: 1 }}>
        <MaterialReactTable table={table} />
      </Box>
    </Box>
  )
}

export default MRTDataGridV2
